import { siteConfig, type Project } from "../config/site";
import { Icon } from "./Icon";
import { FormattedText } from "./FormattedText";

type ProjectsSectionProps = {
  readonly projects: readonly Project[];
};

export function ProjectsSection({ projects }: ProjectsSectionProps) {
  if (projects.length === 0) return null;

  return (
    <section
      id="sites"
      className="border-t border-ink/15"
      aria-labelledby="sites-title"
    >
      <div className="mx-auto w-full max-w-[1600px] px-[clamp(22px,4.4vw,72px)] py-[clamp(64px,8vw,112px)]">
        <div className="mb-12 sm:mb-16">
          <h2 id="sites-title" className="section-title">
            {siteConfig.projects.heading}
          </h2>
          <p className="section-intro max-w-[60ch]">
            <FormattedText>{siteConfig.projects.intro}</FormattedText>
          </p>
        </div>
        <div className="space-y-12 sm:space-y-16">
          {projects.map((project) => (
            <article
              key={project.name}
              className={`grid items-start gap-8 border-t border-ink/15 pt-8 first:border-t-0 first:pt-0 sm:gap-12 sm:pt-12 sm:first:pt-0 ${project.image ? "min-[901px]:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]" : ""}`}
            >
              {project.image && (
                <div className="aspect-[16/10] overflow-hidden bg-ink/[.035]">
                  <img
                    src={project.image.src}
                    alt={project.image.alt}
                    width="960"
                    height="600"
                    loading="lazy"
                    decoding="async"
                    className={`size-full ${project.image.fit === "contain" ? "object-contain p-[12%]" : "object-cover"}`}
                  />
                </div>
              )}
              <div className="min-w-0">
                <h3 className="text-[clamp(26px,3vw,40px)] font-medium tracking-[-.04em] [overflow-wrap:anywhere]">
                  {project.href ? (
                    <a
                      className="group inline-flex min-h-11 items-center gap-5 transition-colors hover:text-accent"
                      href={project.href}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {project.name}
                      <span
                        className="size-5 shrink-0 transition-transform group-hover:translate-x-1"
                        aria-hidden="true"
                      >
                        <Icon name="arrow" />
                      </span>
                    </a>
                  ) : (
                    project.name
                  )}
                </h3>
                <p className="mt-4 max-w-[55ch] text-base leading-7 [overflow-wrap:anywhere] whitespace-pre-line text-ink/70">
                  <FormattedText>{project.description}</FormattedText>
                </p>
                {project.contribution && (
                  <div className="mt-8">
                    <h4 className="font-mono text-[10px] tracking-[.14em] text-ink/60 uppercase">
                      My part
                    </h4>
                    <p className="mt-3 max-w-[55ch] text-[15px] leading-7 [overflow-wrap:anywhere] whitespace-pre-line text-ink/80">
                      <FormattedText>{project.contribution}</FormattedText>
                    </p>
                  </div>
                )}
                {project.skills && project.skills.length > 0 && (
                  <div
                    aria-label="Skills used"
                    className="mt-7 flex flex-wrap gap-x-5 gap-y-2 font-mono text-xs leading-6 text-accent"
                  >
                    {project.skills.map((skill) => (
                      <span
                        key={skill}
                        className="border-b border-accent/25 pb-1 [overflow-wrap:anywhere]"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
